/**
 * Acquisition-source (`aft`) display labels for the Players page.
 *
 * A hook rather than a plain map because the i18n extractor only harvests LITERAL
 * translate call sites: a module-scope label map read through a dynamic lookup would
 * compile and render, but the copy would never reach `src/locales/*` + the check
 * gate would never know it was missing. The switch below keeps every label literal.
 *
 * (Keep this comment free of literal translate-call syntax: the extractor pre-scans
 * comments too, and a fake call site in one makes it skip the whole file silently.)
 *
 * `aft` is an open set the backend grows (`direct`, `organic`, `affiliate`, `web`,
 * `app`, `crypto`, `video`, `influencers`, ...), so an unrecognised literal falls
 * back to itself: the field shows the raw source rather than going blank.
 *
 * One affiliate's players carry their own source value (see `affiliateSource`), named from `affiliateNames` (the
 * brand's affiliates by number) once it is read, by number until then.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { OWNER_LINK, UNATTRIBUTED, affiliateOfSource } from './roster-view';

export type SourceLabeller = (source: string) => string;

const NO_NAMES: ReadonlyMap<string, string> = new Map();

export const useSourceLabel = (affiliateNames: ReadonlyMap<string, string> = NO_NAMES): SourceLabeller => {
  const { t } = useTranslation();
  return React.useCallback(
    (source: string): string => {
      const affiliateNo = affiliateOfSource(source);
      if (affiliateNo !== undefined) {
        if (affiliateNo === '') return t('Unknown affiliate');
        const name = affiliateNames.get(affiliateNo);
        return name ? t('Affiliate: {{name}}', { name }) : t('Affiliate #{{n}}', { n: affiliateNo });
      }
      switch (source) {
        case 'direct':
          return t('Direct');
        case 'organic':
          return t('Organic');
        case 'affiliate':
          return t('Affiliate');
        case 'affiliates':
          return t('Affiliates');
        case 'web':
          return t('Web');
        case 'app':
          return t('Apps');
        case 'crypto':
          return t('Crypto');
        case 'video':
          return t('Short Videos');
        case 'influencers':
          return t('Influencers');
        case OWNER_LINK:
          return t('Owner link');
        case UNATTRIBUTED:
          return t('Not attributed');
        default:
          return source;
      }
    },
    [t, affiliateNames],
  );
};

export default useSourceLabel;
